import React, { useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import './AccueilLayout.css';
import AccueilPage from './AccueilPage';
import MSNPage from './MSNPage';
import PinterestPage from './PinterestPage';
import AirbnbPage from './AirbnbPage';
import SpotifyPage from './SpotifyPage';
import YoutubePage from './YoutubePage';

const TABS = [
  { id: 'calendrier', label: 'Calendrier' },
  { id: 'msn',        label: 'MSN'        },
  { id: 'pinterest',  label: 'Pinterest'  },
  { id: 'airbnb',     label: 'Airbnb'     },
  { id: 'spotify',    label: 'Spotify'    },
  { id: 'youtube',    label: 'Youtube'    },
];

function getModalComponents(team: Team): Record<string, React.ReactNode> {
  return {
    msn:     <MSNPage team={team} />,
    airbnb:  <AirbnbPage />,
    spotify: <SpotifyPage />,
    youtube: <YoutubePage />,
  };
}

export type Team = 'edouard' | 'juliette';

export default function AccueilLayout() {
  const location = useLocation();
  const team: Team = (location.state as any)?.team === 'juliette' ? 'juliette' : 'edouard';
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [msnVisited, setMsnVisited] = useState(false);

  const openModal = useCallback((id: string) => {
    setActiveModal(id);
    if (id === 'msn') setMsnVisited(true);
  }, []);

  const handleTabClick = useCallback((id: string) => {
    if (id === 'calendrier') {
      setActiveModal(null);
    } else {
      openModal(id);
    }
  }, [openModal]);

  const modalComponents = getModalComponents(team);
  /* Pinterest is NOT a modal — it's an overlay of polaroids on the calendar */
  const showPinterest = activeModal === 'pinterest';
  /* Other tabs use the sketch-window modal */
  const showModal = activeModal && modalComponents[activeModal];

  return (
    <div className="accueil-layout">
      <nav className="tab-nav">
        {TABS.map(({ id, label }) => (
          <button
            key={id}
            className={`tab-nav__item${
              (id === 'calendrier' && !activeModal) || id === activeModal
                ? ' tab-nav__item--active'
                : ''
            }`}
            onClick={() => handleTabClick(id)}
          >
            {label}
          </button>
        ))}
      </nav>

      <div className="accueil-layout__content">
        {/* Calendar is always rendered */}
        <AccueilPage onOpenModal={openModal} msnVisited={msnVisited} team={team} />

        {/* Pinterest polaroids overlay (no modal window) */}
        {showPinterest && (
          <div className="pinterest-overlay" onClick={() => setActiveModal(null)}>
            <PinterestPage />
          </div>
        )}

        {/* Modal overlay for other tabs */}
        {showModal && (
          <div className="tab-modal-overlay" onClick={() => setActiveModal(null)}>
            <div className="tab-modal-window" onClick={e => e.stopPropagation()}>
              {/* Inner container: border + content clipped inside */}
              <div className="tab-modal-inner">
                {/* Sketch outer border */}
                <svg className="tab-modal-window__border" viewBox="0 0 800 600" preserveAspectRatio="none">
                  <path
                    d="M12,6 C200,3 600,8 788,5 C793,8 797,13 798,18 C800,150 797,450 799,582 C797,587 793,592 788,594 C580,597 220,593 12,596 C7,593 3,588 2,582 C1,420 3,120 1,18 C3,13 7,8 12,6 Z"
                    fill="none" stroke="#222" strokeWidth="2.5"
                  />
                </svg>

                {/* Sketch title bar */}
                <div className="tab-modal-titlebar">
                  <svg className="tab-modal-titlebar__border" viewBox="0 0 800 50" preserveAspectRatio="none">
                    <path
                      d="M10,4 C200,2 600,6 790,4 C794,6 797,10 798,14 C799,22 798,34 799,40 C798,44 794,47 790,48 C580,50 220,47 10,49 C6,47 3,44 2,40 C1,28 3,16 2,14 C3,10 6,6 10,4 Z"
                      fill="none" stroke="#222" strokeWidth="2.2"
                    />
                  </svg>
                  <div className="tab-modal-titlebar__content">
                    <span className="tab-modal-titlebar__title">
                      {TABS.find(t => t.id === activeModal)?.label}
                    </span>
                    <div className="tab-modal-titlebar__buttons">
                      <span className="tab-modal-titlebar__btn">_</span>
                      <span className="tab-modal-titlebar__btn">&#x25A1;</span>
                      <button
                        className="tab-modal-titlebar__btn tab-modal-titlebar__btn--close"
                        onClick={() => setActiveModal(null)}
                      >
                        x
                      </button>
                    </div>
                  </div>
                </div>

                {/* Window body */}
                <div className="tab-modal-body">
                  {modalComponents[activeModal!]}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
